import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import type { RoomPhaseView } from "@wr/game-core";
import { matchPath, useLocation } from "react-router";
import { useBalance } from "./BalanceProvider";
import { useGameRoom, type GameRoomController } from "../hooks/useGameRoom";
import { mySeat } from "../lib/room";

/**
 * "Does this player currently hold a seat?" — asked app-wide.
 *
 * ── Why it exists (design spec §14.1 requirement 5, LOCKED) ─────────────────
 *
 * > Conversion is suppressed while a seat is held. Revoking the session
 * > mid-room is a seat loss, and §13.2 says a seat loss is a different failure
 * > with a different remedy — we must not manufacture one.
 *
 * The conversion entry points (§14: the flyout card, the `/welcome` guest card,
 * the guest log-out dialog, any blocked action) are scattered across the chrome
 * and the identity gate, none of which is the screen holding the seat. So the
 * fact has to be app-level, and it is read here rather than passed down through
 * four components that have nothing else to do with rooms.
 *
 * ── LOCKED rule 21 (§16c A18), and what S4-7 did to it ─────────────────────
 *
 * `SeatProvider` is the **only** writer of seat state in `apps/web`; `seatHeld`
 * is derived from a live `Room` whose seat the server has acknowledged, never
 * from a route test or a client-only flag; and **consumers do not change when
 * the writer does**. A second writer is an architecture finding.
 *
 * S4-4 widened the shape once, additively, to
 * `{ seatHeld, roomId, roomConfigId, phase, sessionId }` and predicted that
 * "when S4-5 lands, `useDevRoom` is replaced on line 1 of this component and
 * nothing else in `apps/web` moves". **That is what happened here**, one ticket
 * later than predicted because the room screen and its controller are the same
 * ticket: the controller is now `useGameRoom`, `roomConfigId` and `phase` are
 * real, and the four components that read `seatHeld` are untouched in the diff.
 *
 * ── §8.4's suppression rule, which is a *derivation* change, not a shape one ─
 *
 * > **LOCKED — this is where the guest conversion suppression lifts.**
 * > `SeatProvider`'s widened `phase` field is what the suppression must test —
 * > **not** the mere existence of a `Room` object. Suppression is ON for
 * > `filling`, `ready_check`, `round`, `between_rounds`; OFF for `results`,
 * > `cancelled`, `closed` and every terminal state.
 *
 * At `results` the match is over, the prize is credited and leaving costs
 * nothing — it is the second-highest-value conversion moment in the product
 * after guest log-out, and a screen that still refused to open the sheet would
 * be suppressing on the wrong fact. So `seatHeld` narrows here and every
 * consumer keeps its one boolean.
 *
 * ── What left ──────────────────────────────────────────────────────────────
 *
 * `/dev/room` stopped being a seat route at S4-7 (T-210's recorded deviation:
 * it kept a `PingRoom` seat, which has no config row and no phase machine, so
 * holding the app's one seat controller open for a developer surface would have
 * meant either two writers or a `PingRoom` deciding whether a guest may create
 * an account). §16f **A60** then deleted the screen and the room outright.
 *
 * **This provider stays** — A60 condition 3 is explicit that only the dev screen
 * dies. LOCKED rule 21 is unaffected: S4-4 swapped the writer to the real room
 * controller, and `/room/:roomId` is the whole seat-route list below.
 */

/**
 * Routes on which holding a seat is the point. Everything else frees it.
 *
 * `/room/:roomId` is the whole list now: one route, one mounted component, for
 * the entire lifecycle (§2, LOCKED — the phase transitions never navigate).
 */
const SEAT_ROUTES = ["/room/:roomId"];

/**
 * §8.4 LOCKED: the phases in which a seat suppresses conversion. `results` and
 * every terminal are deliberately absent — the match is over and leaving costs
 * nothing.
 *
 * `ReadonlySet<RoomPhaseView>` per A117: these are the **in-room** phases off
 * the socket, and typing the set against `@wr/game-core` is what makes a rename
 * there break this file rather than quietly widen §8.4's four to three. The
 * lookup casts because the synced `phase` is a bare `string` on the wire and an
 * unrecognised one must fall through as "not suppressing", not fail to compile.
 */
const SUPPRESSING_PHASES: ReadonlySet<RoomPhaseView> = new Set([
  "filling",
  "ready_check",
  "round",
  "between_rounds",
]);

export interface SeatState {
  /**
   * True while the player occupies a seat **in a money-at-risk or pre-game
   * phase**, including while reconnecting.
   *
   * §13.2's matrix keeps the seat while the socket is down, which is exactly the
   * window in which revoking the session would be most destructive — so
   * `reconnecting` still counts.
   *
   * **A seat the server has ended does not count** (T-461): "occupies" is the
   * word, and a `released` or `forfeited` participant occupies nothing even
   * while the room around them keeps playing. See the derivation below.
   */
  seatHeld: boolean;
  /** Our `rooms.id` for the held seat, or `null`. The spent-room set's space. */
  roomId: string | null;
  /** The `room_configs.id` behind the held room — what `Play again` re-joins. */
  roomConfigId: string | null;
  /** The room's in-room phase, or `null` when there is no room. */
  phase: string | null;
  /** The session id the server acknowledged the seat under, or `null`. */
  sessionId: string | null;
  /** The controller. Consumed by the room screen, which is the screen it is for. */
  room: GameRoomController;
}

const SeatContext = createContext<SeatState | null>(null);

export function SeatProvider({ children }: { children: ReactNode }) {
  /**
   * S4-8: the room socket carries this player's balance channel, and the chip is
   * chrome that outlives the room — so the message goes straight up to
   * `BalanceProvider` rather than into any state this component owns.
   */
  const { applyBalanceMessage, reconcileBalance } = useBalance();
  const room = useGameRoom(applyBalanceMessage);
  const location = useLocation();

  const snapshot = room.snapshot;
  const phase = snapshot?.phase ?? null;

  /**
   * A snapshot means the server put us in a room and has not taken us out; the
   * phase decides whether that seat suppresses anything (§8.4). `seatLost`
   * clears nothing here on purpose — the screen still needs the frozen snapshot
   * to render its terminal panel — so the socket's death is read from the
   * connection instead.
   *
   * ── **T-461: the seat's own outcome is the third input** ────────────────────
   *
   * §8.4's LOCKED enumeration ends *"…and **every terminal state in §9**"*, and
   * the phase alone cannot deliver that. Of the eight terminals, six are already
   * covered by the phase (T1/T7 are `cancelled`, T2 is `closed`, T5/T6/T8 have
   * no snapshot or a `completed` one) — but **T3 and T4 are exactly the two that
   * happen to a seat inside a still-running room.** `selectRoomState` rank 7
   * decides T4 from `seatOutcome === 'released'` on a snapshot whose phase is
   * still `filling` or `ready_check`, and its own note says the room *"may be
   * three rounds deep"* by the time that player looks. So a released player sat
   * behind a `seatHeld === true` that this field's own docstring already denies:
   * *"true while the player **occupies** a seat"*.
   *
   * Found by T-461 building §882 row 5's guest CTA on T4: the conversion sheet
   * would not open, because `OverlayProvider.openConversion`'s §14.1 r5 guard
   * reads this boolean. **A guest CTA that opens nothing is the dead control
   * §886 rules is worse than no button**, so this predicate — not the call site
   * — is where it is fixed: a second, local answer at one consumer is exactly
   * the second writer LOCKED rule 21 forbids.
   *
   * `status` is the same field `selectRoomState`'s own `ended` predicate reads,
   * for the same reason: it is the server's statement about *this participant*,
   * independent of what the room is doing around them.
   */
  const seat = mySeat(snapshot, room.sessionId);
  const seatEnded = seat?.status === "left" || seat?.status === "forfeited";

  const seatHeld =
    snapshot !== null &&
    !room.seatLost &&
    !seatEnded &&
    phase !== null &&
    SUPPRESSING_PHASES.has(phase as RoomPhaseView);

  /**
   * ── LOCKED rule 18's client half: in-app navigation frees the seat ─────────
   *
   * A **closed tab is a drop** (close code 1001) and holds its seat for the
   * whole `reconnect_grace_s` window, because the server cannot tell it from a
   * phone in a lift. In-app navigation is different in kind — the player is
   * demonstrably still here and has chosen to be elsewhere — so it calls
   * `room.leave(true)`, the consented leave, and the seat is released
   * immediately. Those two are different **by design** and the S4-4 DoD tests
   * both.
   *
   * **This is also how a mid-game forfeit is performed** (§10.2): the dialog's
   * confirm does the navigation the player originally asked for, the navigation
   * lands here, and the server's `onLeave` records `forfeit` and refunds
   * nothing. There is deliberately no second implementation of that transition
   * — the api's leave route refuses a mid-game leave for the same reason.
   *
   * It lives here because `SeatProvider` is the only thing that outlives the
   * screen. Putting it in a screen's unmount loses it the next time that screen
   * is restyled — which is precisely how T-174's hoist came about.
   */
  const reset = room.reset;
  const wasOnSeatRoute = useRef(false);
  const onSeatRoute = SEAT_ROUTES.some((pattern) => matchPath(pattern, location.pathname) !== null);
  /**
   * **The latest reconcile, held by ref so the transition's dependency list is
   * the transition** (T-475). `reconcileBalance` is `useCallback(…, [reload])`
   * over `useSession()`'s `reload`; in the product that identity is stable, but
   * listing it here would make a *route-exit* effect re-run on a *session*
   * re-render, and the one thing this effect must be keyed to is the route
   * change. The deps stay exactly what they were before this ticket.
   */
  const reconcileRef = useRef(reconcileBalance);
  reconcileRef.current = reconcileBalance;

  useEffect(() => {
    const left = wasOnSeatRoute.current && !onSeatRoute;
    wasOnSeatRoute.current = onSeatRoute;
    if (!left) return;
    /**
     * Fire-and-forget on purpose. A failed leave is not a user-facing failure —
     * the fallbacks below it are real, so the worst case is the slow path rather
     * than a stuck seat. Surfacing it would put an error on a screen the player
     * has already left.
     *
     * ── **T-474 / A129 §2.2: that reassurance used to be false on one path** ───
     *
     * It read *"the server still holds the seat for the grace window and then
     * releases it"*, and during the **connect window** there is no grace window,
     * because there is no socket: `detach()`'s `if (!room) return` meant this
     * effect did nothing whatsoever, and the worst case was 5 m 25 s with a paid
     * tile locked behind a ghost (T-470 §3(c), production room `11afc7f8`).
     *
     * A129's instruction was *"make the comment true, per A128's precedent — do
     * not merely correct it"*, so the sentence is left standing and `reset()` now
     * earns it: with a socket the consented close releases the seat and the
     * server's `onLeave` refunds; without one, `detach()` sends the api's leave
     * route, which refunds in the same transaction. The two fallbacks are the
     * room's own in-band expiry sweep (T-472) and the cron behind that.
     */
    reset();
    /**
     * ── **T-475 / A129 call 3 arm 1: one authoritative read on the way out** ───
     *
     * > *"The client reconciles on return to the lobby from a seat route.
     * > `SeatProvider.tsx:198–209` already detects precisely that transition and
     * > already calls `reset()` there. One `void reconcileBalance()` beside it.
     * > **This is the guarantee that does not depend on Redis at all**."*
     *
     * The defect it closes is not hypothetical and it is not a chip nicety: for
     * as long as the cron services have existed, `REDIS_URL` was absent on all
     * four of them (T-471 §2.1), so every abandoned-seat refund and every
     * reconciled welcome grant published to nobody — and `useLobby` is
     * `refetchInterval: false` *by design*, so "the chip catches up on the next
     * authoritative read" meant "on a reload the player has no reason to
     * perform". T-470 measured 368 s of a wrong number with no end.
     *
     * **It introduces no new balance source, and that is LOCKED.** The chip has
     * exactly two: the authoritative session read and an ordered
     * `BalanceMessage`. `reconcileBalance` *is* the first of those — the same
     * `reload()` → `players.me` primitive the settlement and resubscribe paths
     * already fire — so this is a third **trigger**, not a third source. The
     * lobby fetch's `viewer.balanceCoins` is still **not** piped into the chip:
     * it carries no `transactionId`, cannot be ordered against the socket, and
     * stays what it is, the tile-affordability input.
     *
     * **Fire-and-forget, like the leave above it.** A failed read is not a
     * user-facing failure: `reconcileBalance` never rejects, holds the last
     * number rather than flashing `—`, and dims (§14.3 part 5). The old number
     * standing silently is the honest outcome — *we do not know* — and putting
     * an error on the lobby about a read the player never asked for would be the
     * second thing on this path that reports a failure nobody can act on.
     *
     * Ordering with `reset()` is deliberate and harmless: the leave it may send
     * refunds asynchronously, so this read can legitimately land *before* the
     * refund row exists. The number it writes is true when it is written, the
     * watermark is reset by the read itself, and the lobby socket's A76 join
     * snapshot — or the published refund — carries the client forward from
     * there. Where there is no socket at all (the case this exists for), the
     * player is at worst one authoritative read behind instead of unboundedly
     * stale, and their next return re-reads.
     */
    void reconcileRef.current();
  }, [onSeatRoute, reset]);

  const value = useMemo<SeatState>(
    () => ({
      seatHeld,
      roomId: snapshot?.roomId ?? null,
      roomConfigId: room.roomConfigId,
      phase,
      sessionId: room.sessionId,
      room,
    }),
    [seatHeld, snapshot, phase, room],
  );

  return <SeatContext.Provider value={value}>{children}</SeatContext.Provider>;
}

export function useSeat(): SeatState {
  const value = useContext(SeatContext);
  if (!value) throw new Error("useSeat must be used inside <SeatProvider>");
  return value;
}
